'use client';

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { LinkProps } from 'next/link';
import { useTranslations } from 'next-intl';
import { useInternalPathname } from 'hooks/navigation/useInternalPathname';
import { RightArrow, UHLogo, UHLogoWhite } from 'assets/icons';
import CustomLink, { useFormatLink } from 'components/shared/CustomLink';
import { CartIcon } from 'components/shared/Header/Cart';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { PLCTA } from './PLCTA';
import {
  getPerformanceLabNavSection,
  PERFORMANCE_LAB_NAV_PATHS,
  type PerformanceLabNavSection,
} from './performanceLabNavRoutes';
import {
  PL_HERO_NAV_INLINE_PAD_MOBILE_PX,
  PL_NAVBAR_HIDE_TRANSITION_MS,
  PL_NAVBAR_MORPH_TRANSITION_MS,
  PL_NAVBAR_OFFSET_CSS_VAR,
  getPerformanceLabHeroNavHorizontalPadPx,
  syncPerformanceLabNavbarOffset,
} from './utils';
import { useOpenCallbackModal } from './hooks';

import { useGlpSignUp } from './glp/useGlpSignUp';
import { getPerformanceLabLenis } from 'utils/performanceLabLenisRegistry';
import styles from './PerformanceLabNavbar.module.css';

const SCROLL_TOP_THRESHOLD = 48;
/** Hysteresis band avoids frosted/inset flicker when scroll hovers near the hero edge. */
const SCROLL_FROST_ON = 40;
const SCROLL_FROST_OFF = 8;

export const PerformanceLabNavbar: React.FC = () => {
  const t = useTranslations('performance-lab');
  const internalPathname = useInternalPathname();
  const routePath =
    internalPathname.length > 1
      ? internalPathname.replace(/\/$/, '')
      : internalPathname;
  const { regionSlug } = useRegionLocale();
  const formatLink = useFormatLink();
  const [visible, setVisible] = useState(true);
  const [scrolled, setScrolled] = useState(false);
  const [navHorizontalPadPx, setNavHorizontalPadPx] = useState(
    PL_HERO_NAV_INLINE_PAD_MOBILE_PX,
  );
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const navInnerRef = useRef<HTMLDivElement>(null);
  const lastScrollY = useRef(0);
  const scrolledRef = useRef(false);
  const ticking = useRef(false);
  const visibleRef = useRef(true);
  const offsetTransitionRafRef = useRef(0);

  const activeSection = getPerformanceLabNavSection(routePath);
  const isBuyPage = routePath === '/performance-lab/buy';
  const isLightNav = activeSection === 'contact' || isBuyPage;
  const isLandingPage =
    activeSection === 'diagnostics' ||
    activeSection === 'recovery' ||
    activeSection === 'ivLounge' ||
    activeSection === 'glp';
  const isFrosted = scrolled || isBuyPage || isLandingPage;
  const showBackdrop = isFrosted || mobileMenuOpen;
  const isHeroInset = false;

  const syncHeroNavMorph = useCallback(() => {
    const progress = 1;
    const padPx = getPerformanceLabHeroNavHorizontalPadPx(
      progress,
      window.innerWidth,
    );
    setNavHorizontalPadPx(padPx);
  }, []);

  const diagnosticsHref = useMemo(
    () =>
      formatLink(PERFORMANCE_LAB_NAV_PATHS.diagnostics, {
        asObject: true,
      }) as LinkProps['href'],
    [formatLink],
  );

  const recoveryHref = useMemo(
    () =>
      formatLink(PERFORMANCE_LAB_NAV_PATHS.recovery, {
        asObject: true,
      }) as LinkProps['href'],
    [formatLink],
  );

  const ivLoungeHref = useMemo(
    () =>
      formatLink(PERFORMANCE_LAB_NAV_PATHS.ivLounge, {
        asObject: true,
      }) as LinkProps['href'],
    [formatLink],
  );

  const glpHref = useMemo(
    () =>
      formatLink(PERFORMANCE_LAB_NAV_PATHS.glp, {
        asObject: true,
      }) as LinkProps['href'],
    [formatLink],
  );

  const contactHref = useMemo(
    () =>
      formatLink(PERFORMANCE_LAB_NAV_PATHS.contact, {
        asObject: true,
      }) as LinkProps['href'],
    [formatLink],
  );

  const handleBookCallback = useOpenCallbackModal();
  const handleGlpSignUp = useGlpSignUp();

  const homeHref = useMemo(
    () =>
      formatLink(`/${regionSlug}/`, { asObject: true }) as LinkProps['href'],
    [formatLink, regionSlug],
  );

  useEffect(() => {
    visibleRef.current = visible;
  }, [visible]);

  const syncNavbarOffset = useCallback(() => {
    syncPerformanceLabNavbarOffset(navRef.current, navInnerRef.current);
  }, []);

  const runOffsetSyncDuringTransition = useCallback(() => {
    cancelAnimationFrame(offsetTransitionRafRef.current);
    syncNavbarOffset();

    const startedAt = performance.now();
    const duration =
      Math.max(PL_NAVBAR_HIDE_TRANSITION_MS, PL_NAVBAR_MORPH_TRANSITION_MS) + 50;
    const tick = (now: number) => {
      syncNavbarOffset();
      if (now - startedAt < duration) {
        offsetTransitionRafRef.current = requestAnimationFrame(tick);
      }
    };

    offsetTransitionRafRef.current = requestAnimationFrame(tick);
  }, [syncNavbarOffset]);

  const syncScrolledFromScrollY = useCallback((scrollY: number) => {
    let nextScrolled = scrolledRef.current;
    if (scrollY >= SCROLL_FROST_ON) {
      nextScrolled = true;
    } else if (scrollY <= SCROLL_FROST_OFF) {
      nextScrolled = false;
    }

    if (nextScrolled === scrolledRef.current) {
      return false;
    }

    scrolledRef.current = nextScrolled;
    setScrolled(nextScrolled);
    return true;
  }, []);

  useEffect(() => {
    syncNavbarOffset();

    const resizeObserver = new ResizeObserver(syncNavbarOffset);
    if (navRef.current) resizeObserver.observe(navRef.current);
    if (navInnerRef.current) resizeObserver.observe(navInnerRef.current);

    window.addEventListener('resize', syncNavbarOffset, { passive: true });

    const lenis = getPerformanceLabLenis();
    lenis?.on?.('scroll', syncNavbarOffset);

    let lenisPollId = 0;
    if (!lenis) {
      lenisPollId = window.setInterval(() => {
        const activeLenis = getPerformanceLabLenis();
        if (!activeLenis) return;
        activeLenis.on?.('scroll', syncNavbarOffset);
        window.clearInterval(lenisPollId);
      }, 100);
    }

    return () => {
      cancelAnimationFrame(offsetTransitionRafRef.current);
      window.clearInterval(lenisPollId);
      resizeObserver.disconnect();
      window.removeEventListener('resize', syncNavbarOffset);
      getPerformanceLabLenis()?.off?.('scroll', syncNavbarOffset);
      document.documentElement.style.removeProperty(PL_NAVBAR_OFFSET_CSS_VAR);
    };
  }, [syncNavbarOffset]);

  useLayoutEffect(() => {
    runOffsetSyncDuringTransition();
  }, [
    visible,
    mobileMenuOpen,
    scrolled,
    isHeroInset,
    runOffsetSyncDuringTransition,
  ]);

  useEffect(() => {
    lastScrollY.current = window.scrollY;
    syncScrolledFromScrollY(window.scrollY);
    syncHeroNavMorph();
    syncNavbarOffset();

    const onScroll = () => {
      if (ticking.current) return;
      ticking.current = true;

      requestAnimationFrame(() => {
        ticking.current = false;

        const currentY = window.scrollY;
        const lastY = lastScrollY.current;
        lastScrollY.current = currentY;

        const scrolledChanged = syncScrolledFromScrollY(currentY);
        syncHeroNavMorph();

        if (currentY < SCROLL_TOP_THRESHOLD) {
          if (!visibleRef.current) {
            visibleRef.current = true;
            setVisible(true);
          }
          syncNavbarOffset();
          runOffsetSyncDuringTransition();
          return;
        }

        const scrollingUp = currentY < lastY;
        if (scrollingUp !== visibleRef.current) {
          visibleRef.current = scrollingUp;
          setVisible(scrollingUp);
          syncNavbarOffset();
          runOffsetSyncDuringTransition();
          return;
        }

        if (scrolledChanged) {
          runOffsetSyncDuringTransition();
        }

        syncNavbarOffset();
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    const onResize = () => syncHeroNavMorph();
    window.addEventListener('resize', onResize, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, [
    runOffsetSyncDuringTransition,
    syncHeroNavMorph,
    syncNavbarOffset,
    syncScrolledFromScrollY,
  ]);

  useEffect(() => {
    syncHeroNavMorph();
  }, [mobileMenuOpen, syncHeroNavMorph, isLightNav]);

  const toggleMobileMenu = useCallback(() => {
    setMobileMenuOpen((open) => !open);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  const diagnosticsLabel = t('nav.diagnostics');
  const recoveryLabel = t('nav.recovery');
  const ivLoungeLabel = t('nav.ivLounge');
  const glpLabel = t('nav.glpProgram');
  const contactLabel = t('nav.contactUs');
  const isGlpPage = activeSection === 'glp';
  const handleNavCta = isGlpPage ? handleGlpSignUp : handleBookCallback;
  const navCtaLabel = isGlpPage ? t('nav.signUp') : t('nav.bookCallback');
  const navCtaAria = isGlpPage ? t('nav.signUpAria') : t('nav.bookCallbackAria');

  const renderActiveTabLabel = (label: string) => (
    <span className={styles.activeTabLabel}>
      <span className={styles.activeTabDot} aria-hidden>•</span>
      {label}
    </span>
  );

  const renderDesktopTab = (
    section: PerformanceLabNavSection,
    label: string,
    href: LinkProps['href'],
  ) => {
    const isActive = activeSection === section;
    if (isActive) {
      return (
        <span
          className={styles.tabActive}
          data-light={isLightNav ? 'true' : undefined}
          role="listitem"
          aria-current="page"
        >
          {renderActiveTabLabel(label)}
        </span>
      );
    }

    return (
      <CustomLink
        className={styles.tabLink}
        data-light={isLightNav ? 'true' : undefined}
        role="listitem"
        href={href}
      >
        {label}
      </CustomLink>
    );
  };

  const renderMobileTab = (
    section: PerformanceLabNavSection,
    label: string,
    href: LinkProps['href'],
  ) => {
    const isActive = activeSection === section;
    return (
      <CustomLink
        className={styles.mobileTabItem}
        data-active={isActive ? 'true' : undefined}
        data-light={isLightNav ? 'true' : undefined}
        role="listitem"
        href={href}
        aria-current={isActive ? 'page' : undefined}
        onClick={closeMobileMenu}
      >
        {isActive ? renderActiveTabLabel(label) : label}
      </CustomLink>
    );
  };

  return (
    <nav
      className={styles.navRoot}
      ref={navRef}
      data-performance-lab-navbar
      aria-label={t('nav.aria')}
    >
      <div
        className={styles.navBackdrop}
        data-active={showBackdrop ? 'true' : undefined}
        data-light={isLightNav ? 'true' : undefined}
        data-menu-open={mobileMenuOpen ? 'true' : undefined}
        data-buy-page={isBuyPage ? 'true' : undefined}
        aria-hidden
      />
      <div
        className={styles.navInner}
        ref={navInnerRef}
        data-menu-open={mobileMenuOpen ? 'true' : 'false'}
        data-hero-inset={isHeroInset ? 'true' : undefined}
        style={{
          paddingLeft: `${navHorizontalPadPx}px`,
          paddingRight: `${navHorizontalPadPx}px`,
        }}
      >
        <div className={styles.topRow}>
          <button
            type="button"
            className={styles.menuToggle}
            data-light={isLightNav ? 'true' : undefined}
            aria-label={t('nav.menuAria')}
            aria-expanded={mobileMenuOpen}
            aria-controls="pl-nav-mobile-tabs"
            onClick={toggleMobileMenu}
          >
            <span
              className={styles.menuIcon}
              data-open={mobileMenuOpen ? 'true' : undefined}
              data-light={isLightNav ? 'true' : undefined}
              aria-hidden
            />
          </button>

          <CustomLink
            className={styles.logoLink}
            href={homeHref}
            aria-label={t('nav.homeAria')}
            data-light={isLightNav ? 'true' : undefined}
            data-show-label={mobileMenuOpen ? 'true' : undefined}
            onClick={closeMobileMenu}
          >
            {isLightNav ? (
              <UHLogo fill="#000" aria-hidden />
            ) : (
              <UHLogoWhite aria-hidden />
            )}
            <span
              className={styles.homeBackLabel}
              data-visible={mobileMenuOpen ? 'true' : undefined}
              data-light={isLightNav ? 'true' : undefined}
            >
              {t('nav.backHome')}
            </span>
          </CustomLink>

          <div className={styles.desktopTabs} role="list">
            {renderDesktopTab('diagnostics', diagnosticsLabel, diagnosticsHref)}
            {renderDesktopTab('recovery', recoveryLabel, recoveryHref)}
            {renderDesktopTab('ivLounge', ivLoungeLabel, ivLoungeHref)}
            {renderDesktopTab('glp', glpLabel, glpHref)}
            {renderDesktopTab('contact', contactLabel, contactHref)}
          </div>

          <div className={styles.rightActions}>
            <PLCTA
              className={styles.exploreCta}
              type="button"
              onClick={handleNavCta}
              aria-label={navCtaAria}
              data-buttontype={
                isGlpPage
                  ? 'performance_lab_nav_sign_up'
                  : 'performance_lab_nav_book_callback'
              }
            >
              {navCtaLabel}
            </PLCTA>
            {isBuyPage && (
              <CartIcon iconTheme={isLightNav ? 'light' : 'dark'} />
            )}
          </div>
        </div>

        <div
          className={styles.mobileTabsRow}
          id="pl-nav-mobile-tabs"
          role="list"
          aria-label={t('nav.menuAria')}
          data-open={mobileMenuOpen ? 'true' : undefined}
          data-light={isLightNav ? 'true' : undefined}
        >
          {renderMobileTab('diagnostics', diagnosticsLabel, diagnosticsHref)}
          {renderMobileTab('recovery', recoveryLabel, recoveryHref)}
          {renderMobileTab('ivLounge', ivLoungeLabel, ivLoungeHref)}
          {renderMobileTab('glp', glpLabel, glpHref)}
          {renderMobileTab('contact', contactLabel, contactHref)}
          <hr
            className={styles.mobileMenuDivider}
            data-light={isLightNav ? 'true' : undefined}
            aria-hidden
          />
          <CustomLink
            className={styles.mobileTabItem}
            data-light={isLightNav ? 'true' : undefined}
            role="listitem"
            href={homeHref}
            onClick={closeMobileMenu}
          >
            <RightArrow className={styles.backArrow} fill="currentColor" />
            {t('nav.backHome')}
          </CustomLink>
        </div>
      </div>
    </nav>
  );
};
